import React, { useState, useEffect } from 'react';
import { frontendDb as db } from '../../lib/frontend-db';

interface BieberInfoSettings {
  bieber_info_title: string;
  bieber_info_subtitle: string;
  bieber_info_hero_image: string;
  bieber_info_content: string;
  bieber_info_contact: string;
  bieber_info_gallery: string;
  bieber_info_history: string;
  bieber_info_activities: string;
  bieber_imprint_content: string;
  bieber_privacy_content: string;
  bieber_footer_bg_color: string;
  bieber_footer_text_color: string;
  bieber_footer_link_color: string;
  bieber_footer_link_hover_color: string;
  bieber_back_to_stimmart_text: string;
}

const BieberInfoPage = () => {
  const [settings, setSettings] = useState<BieberInfoSettings>({
    bieber_info_title: 'Liederkranz Bieber',
    bieber_info_subtitle: 'Tradition und Musik seit 1847',
    bieber_info_hero_image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&h=600&fit=crop',
    bieber_info_content: 'Der Liederkranz Bieber 1847 e.V. blickt auf eine lange Tradition zurück. Seit über 175 Jahren pflegen wir das deutsche Liedgut und bringen Menschen durch Musik zusammen.',
    bieber_info_contact: 'Kontakt:\nLiederkranz Bieber 1847 e.V.\nWürzburger Straße 1\n63599 Biebergemünd\nTelefon: 06050 / 1234567\nE-Mail: info@liederkranz-bieber.de',
    bieber_info_gallery: JSON.stringify([
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop',
      'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=300&fit=crop'
    ]),
    bieber_info_history: 'Unsere Geschichte reicht bis ins Jahr 1847 zurück und ist geprägt von musikalischen Höhepunkten und gesellschaftlichen Ereignissen. Über die Jahrzehnte haben wir uns zu einem wichtigen kulturellen Bestandteil der Gemeinde entwickelt.',
    bieber_info_activities: 'Wir bieten regelmäßige Proben, Konzerte und gesellschaftliche Veranstaltungen. Neue Mitglieder sind herzlich willkommen und können jederzeit zu unseren Proben kommen.',
    bieber_imprint_content: '',
    bieber_privacy_content: '',
    bieber_footer_bg_color: '#2d5a2f',
    bieber_footer_text_color: '#ffffff',
    bieber_footer_link_color: '#ffffff',
    bieber_footer_link_hover_color: 'rgba(255, 255, 255, 0.8)',
    bieber_back_to_stimmart_text: 'Entdecken Sie auch unseren Hauptverein StimmArt und erfahren Sie mehr über unsere gemeinsamen musikalischen Aktivitäten und Veranstaltungen.'
  });
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [newGalleryImage, setNewGalleryImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const siteSettings = await db.getSettings();
      if (siteSettings) {
        const bieberSettings = {
          bieber_info_title: (siteSettings as any).bieber_info_title || settings.bieber_info_title,
          bieber_info_subtitle: (siteSettings as any).bieber_info_subtitle || settings.bieber_info_subtitle,
          bieber_info_hero_image: (siteSettings as any).bieber_info_hero_image || settings.bieber_info_hero_image,
          bieber_info_content: (siteSettings as any).bieber_info_content || settings.bieber_info_content,
          bieber_info_contact: (siteSettings as any).bieber_info_contact || settings.bieber_info_contact,
          bieber_info_gallery: (siteSettings as any).bieber_info_gallery || settings.bieber_info_gallery,
          bieber_info_history: (siteSettings as any).bieber_info_history || settings.bieber_info_history,
          bieber_info_activities: (siteSettings as any).bieber_info_activities || settings.bieber_info_activities,
          bieber_imprint_content: (siteSettings as any).bieber_imprint_content || settings.bieber_imprint_content,
          bieber_privacy_content: (siteSettings as any).bieber_privacy_content || settings.bieber_privacy_content,
          bieber_footer_bg_color: (siteSettings as any).bieber_footer_bg_color || settings.bieber_footer_bg_color,
          bieber_footer_text_color: (siteSettings as any).bieber_footer_text_color || settings.bieber_footer_text_color,
          bieber_footer_link_color: (siteSettings as any).bieber_footer_link_color || settings.bieber_footer_link_color,
          bieber_footer_link_hover_color: (siteSettings as any).bieber_footer_link_hover_color || settings.bieber_footer_link_hover_color,
          bieber_back_to_stimmart_text: (siteSettings as any).bieber_back_to_stimmart_text || settings.bieber_back_to_stimmart_text,
        };
        setSettings(bieberSettings);
        
        try {
          const gallery = JSON.parse(bieberSettings.bieber_info_gallery);
          setGalleryImages(Array.isArray(gallery) ? gallery : []);
        } catch {
          setGalleryImages([]);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Fehler beim Laden der Bieber-Info Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const updatedSettings = {
        ...settings,
        bieber_info_gallery: JSON.stringify(galleryImages)
      };

      await db.updateSettings(updatedSettings);
      window.location.reload();
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Fehler beim Speichern der Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const imageUrl = await db.storeImage(file);
      setSettings(prev => ({
        ...prev,
        bieber_info_hero_image: imageUrl
      }));
    } catch (error) {
      console.error('Error uploading hero image:', error);
      setError('Fehler beim Hochladen des Hero-Bildes');
    }
  };

  const handleGalleryImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setNewGalleryImage(file);
    try {
      const imageUrl = await db.storeImage(file);
      setGalleryImages(prev => [...prev, imageUrl]);
      setNewGalleryImage(null);
    } catch (error) {
      console.error('Error uploading gallery image:', error);
      setError('Fehler beim Hochladen des Galerie-Bildes');
    }
  };

  const removeGalleryImage = (index: number) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Liederkranz Bieber - Einstellungen</h1>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-600">Einstellungen erfolgreich gespeichert!</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Hero Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Hauptbereich (Titel und Bild)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Haupttitel</label>
                <input
                  type="text"
                  value={settings.bieber_info_title}
                  onChange={(e) => setSettings(prev => ({ ...prev, bieber_info_title: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">Untertitel</label>
                <input
                  type="text"
                  value={settings.bieber_info_subtitle}
                  onChange={(e) => setSettings(prev => ({ ...prev, bieber_info_subtitle: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
                />
              </div>
          </div>
                      <div className="mt-4">
              <label className="block text-sm font-medium mb-2 text-gray-700">Hauptbild</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleHeroImageUpload}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
              />
            {settings.bieber_info_hero_image && (
              <img
                src={settings.bieber_info_hero_image}
                alt="Hero"
                className="mt-2 w-32 h-20 object-cover rounded"
              />
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Texte und Inhalte</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Beschreibung</label>
              <textarea
                value={settings.bieber_info_content}
                onChange={(e) => setSettings(prev => ({ ...prev, bieber_info_content: e.target.value }))}
                rows={4}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Vereinsgeschichte</label>
              <textarea
                value={settings.bieber_info_history}
                onChange={(e) => setSettings(prev => ({ ...prev, bieber_info_history: e.target.value }))}
                rows={4}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Aktivitäten und Angebote</label>
              <textarea
                value={settings.bieber_info_activities}
                onChange={(e) => setSettings(prev => ({ ...prev, bieber_info_activities: e.target.value }))}
                rows={4}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
              />
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Kontaktdaten</h2>
          <label className="block text-sm font-medium mb-2 text-gray-700">Kontakttext</label>
          <textarea
            value={settings.bieber_info_contact}
            onChange={(e) => setSettings(prev => ({ ...prev, bieber_info_contact: e.target.value }))}
            rows={6}
            className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
            placeholder={"Kontakt:\nLiederkranz Bieber 1847 e.V.\nWürzburger Straße 1\n63599 Biebergemünd\nTelefon: 06050 / 1234567\nE-Mail: info@liederkranz-bieber.de"}
          />
        </div>

        {/* Contact Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Kontaktdaten</h2>
          <textarea
            value={settings.bieber_info_contact}
            onChange={(e) => setSettings(prev => ({ ...prev, bieber_info_contact: e.target.value }))}
            rows={6}
            className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
            placeholder="Kontaktinformationen..."
          />
        </div>

        {/* Gallery Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Bildergalerie</h2>
                      <div className="mb-4">
              <label className="block text-sm font-medium mb-2 text-gray-700">Neues Bild hinzufügen</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleGalleryImageUpload}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
              />
            </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.map((image, index) => (
              <div key={index} className="relative">
                <img
                  src={image}
                  alt={`Galerie ${index + 1}`}
                  className="w-full h-24 object-cover rounded"
                />
                <button
                  type="button"
                  onClick={() => removeGalleryImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Colors */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Fußbereich-Farben</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Hintergrundfarbe</label>
              <input
                type="color"
                value={settings.bieber_footer_bg_color}
                onChange={(e) => setSettings(prev => ({ ...prev, bieber_footer_bg_color: e.target.value }))}
                className="w-full h-10 border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Textfarbe</label>
              <input
                type="color"
                value={settings.bieber_footer_text_color}
                onChange={(e) => setSettings(prev => ({ ...prev, bieber_footer_text_color: e.target.value }))}
                className="w-full h-10 border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Linkfarbe</label>
              <input
                type="color"
                value={settings.bieber_footer_link_color}
                onChange={(e) => setSettings(prev => ({ ...prev, bieber_footer_link_color: e.target.value }))}
                className="w-full h-10 border border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Link-Hover-Farbe</label>
              <input
                type="color"
                value={settings.bieber_footer_link_hover_color}
                onChange={(e) => setSettings(prev => ({ ...prev, bieber_footer_link_hover_color: e.target.value }))}
                className="w-full h-10 border border-gray-300 rounded"
              />
            </div>
          </div>
        </div>

        {/* Back to StimmArt Text */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Verlinkung zu StimmArt</h2>
          <textarea
            value={settings.bieber_back_to_stimmart_text}
            onChange={(e) => setSettings(prev => ({ ...prev, bieber_back_to_stimmart_text: e.target.value }))}
            rows={3}
            className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
            placeholder="Text für den Zurück-zu-StimmArt-Bereich..."
          />
        </div>

        {/* Legal Content */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4 text-gray-800">Rechtliche Seiten</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Impressum</label>
              <textarea
                value={settings.bieber_imprint_content}
                onChange={(e) => setSettings(prev => ({ ...prev, bieber_imprint_content: e.target.value }))}
                rows={6}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
                placeholder="Impressum-Inhalt..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Datenschutzerklärung</label>
              <textarea
                value={settings.bieber_privacy_content}
                onChange={(e) => setSettings(prev => ({ ...prev, bieber_privacy_content: e.target.value }))}
                rows={6}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 bg-white"
                placeholder="Datenschutzerklärung..."
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BieberInfoPage; 

